import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronLeft, ChevronRight, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Navigation } from "@/components/Navigation";
import { Footer } from "@/components/Footer";
import { API } from "@/lib/api";
import { toast } from "sonner";

const CLOSING_PROMPTS = [
  {
    label: "Var du befinner dig nu",
    heading: "Idag befinner jag mig...",
    key: "current_state",
  },
  {
    label: "Vad du har lärt dig",
    heading: "Det viktigaste jag har förstått om mig själv är...",
    key: "learnings",
  },
  {
    label: "Framåt",
    heading: "Det jag vill ta med mig härifrån är...",
    key: "forward",
  },
];

const BEFORE_AFTER_LABELS = {
  hopes: "Dina förhoppningar i början",
  struggles: "Det du hade problem med",
  goals: "Ditt mål med resan",
};

export default function ClosingCheckinPage() {
  const [answers, setAnswers] = useState({});
  const [openingCheckin, setOpeningCheckin] = useState(null);
  const [saving, setSaving] = useState(false);
  const [step, setStep] = useState(0);
  const [existing, setExisting] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    API.get("/journey/checkin").then(res => {
      if (res.data.completed) setOpeningCheckin(res.data);
    }).catch(() => {});

    API.get("/journey/closing-checkin").then(res => {
      if (res.data.completed) {
        setExisting(res.data);
        setAnswers(res.data.answers || {});
      }
    }).catch(() => {});
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      await API.post("/journey/closing-checkin", { answers });
      toast.success("Avslutande reflektion sparad");
      setStep(CLOSING_PROMPTS.length + 1);
    } catch {
      toast.error("Kunde inte spara");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <Navigation />
      <div className="pt-24 sm:pt-28 pb-8 sm:pb-20 px-4 sm:px-6">
        <div className="max-w-2xl mx-auto">
          <AnimatePresence mode="wait">

            {/* Step 0: Intro */}
            {step === 0 && (
              <motion.div
                key="intro"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                className="text-center pt-8 sm:pt-12"
              >
                <motion.p
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.3 }}
                  className="text-sm uppercase tracking-[0.25em] text-[#A0655A] font-medium mb-6"
                >
                  Avslutande reflektion
                </motion.p>

                <motion.h1
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.5 }}
                  className="heading-font text-3xl sm:text-4xl font-medium text-[#0A0A0A] mb-6 leading-snug"
                  data-testid="closing-checkin-title"
                >
                  Du har gått hela vägen
                </motion.h1>

                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.8 }}
                >
                  <p className="text-[#0A0A0A]/60 text-base max-w-md mx-auto mb-4 leading-relaxed">
                    Innan du avslutar din resa vill jag att du stannar upp en sista gång.
                    Precis som du checkade in i början — checkar du nu ut.
                  </p>
                  <p className="text-[#0A0A0A]/50 max-w-md mx-auto mb-10 leading-relaxed text-sm">
                    Det här är en chans att se hur långt du har kommit och vad du tar med dig härifrån.
                  </p>
                </motion.div>

                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 1.1 }}
                >
                  <Button
                    onClick={() => setStep(1)}
                    className="bg-[#A0655A] hover:bg-[#8B5549] text-white rounded-full px-8 py-6 text-base"
                    data-testid="start-closing-checkin-btn"
                  >
                    Jag är redo
                  </Button>
                </motion.div>
              </motion.div>
            )}

            {/* Steps 1-3: Closing prompts */}
            {step >= 1 && step <= CLOSING_PROMPTS.length && (
              <motion.div
                key={`prompt-${step}`}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
              >
                <p className="text-xs uppercase tracking-[0.2em] text-[#A0655A] font-medium mb-6">
                  {CLOSING_PROMPTS[step - 1].label} · {step} av {CLOSING_PROMPTS.length}
                </p>

                <h2 className="heading-font text-xl sm:text-2xl font-medium text-[#0A0A0A] mb-2 leading-snug">
                  {CLOSING_PROMPTS[step - 1].heading}
                </h2>

                <p className="text-sm text-[#0A0A0A]/30 mb-6">
                  Skriv det som kommer till dig.
                </p>

                <Textarea
                  value={answers[CLOSING_PROMPTS[step - 1].key] || ""}
                  onChange={(e) =>
                    setAnswers((prev) => ({
                      ...prev,
                      [CLOSING_PROMPTS[step - 1].key]: e.target.value,
                    }))
                  }
                  rows={5}
                  className="rounded-xl mb-6 border-[#EBE3DF] focus:border-[#A0655A] resize-none text-base leading-relaxed"
                  data-testid={`closing-answer-${step}`}
                />

                <div className="flex justify-between items-center">
                  <Button
                    onClick={() => setStep(step - 1)}
                    variant="ghost"
                    className="text-[#A0655A]"
                  >
                    <ChevronLeft size={18} className="mr-1" /> Tillbaka
                  </Button>

                  {step < CLOSING_PROMPTS.length ? (
                    <Button
                      onClick={() => setStep(step + 1)}
                      className="bg-[#A0655A] hover:bg-[#8B5549] text-white rounded-full px-6"
                    >
                      Nästa
                      <ChevronRight size={18} className="ml-1" />
                    </Button>
                  ) : (
                    <Button
                      onClick={handleSave}
                      disabled={saving}
                      className="bg-[#A0655A] hover:bg-[#8B5549] text-white rounded-full px-8"
                      data-testid="complete-closing-checkin-btn"
                    >
                      {saving ? "Sparar..." : "Avsluta min reflektion"}
                      <ChevronRight size={18} className="ml-1" />
                    </Button>
                  )}
                </div>
              </motion.div>
            )}

            {/* Step 4: Before/After comparison */}
            {step === CLOSING_PROMPTS.length + 1 && (
              <motion.div
                key="comparison"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
              >
                <div className="text-center mb-10">
                  <p className="text-sm uppercase tracking-[0.25em] text-[#A0655A] font-medium mb-4">
                    Din resa — före och efter
                  </p>
                  <h2 className="heading-font text-2xl sm:text-3xl font-medium text-[#111111]" data-testid="before-after-title">
                    Se hur långt du har kommit
                  </h2>
                </div>

                <div className="space-y-6 mb-10">
                  {openingCheckin?.answers && Object.entries(BEFORE_AFTER_LABELS).map(([key, label]) => (
                    openingCheckin.answers[key] && (
                      <div key={key} className="bg-white border border-[#EBE3DF] rounded-2xl overflow-hidden" data-testid={`comparison-${key}`}>
                        <div className="p-5 border-b border-[#EBE3DF]/50">
                          <p className="text-xs uppercase tracking-wide text-[#A0655A] font-medium mb-2">{label}</p>
                          <p className="text-sm text-[#111111]/60 leading-relaxed italic">
                            "{openingCheckin.answers[key]}"
                          </p>
                        </div>
                        <div className="p-5 bg-[#FDF9F7]">
                          <div className="flex items-center gap-2 mb-2">
                            <ArrowRight size={14} className="text-[#A0655A]" />
                            <p className="text-xs uppercase tracking-wide text-[#A0655A] font-medium">Och nu?</p>
                          </div>
                          {key === "hopes" && answers.learnings && (
                            <p className="text-sm text-[#111111]/70 leading-relaxed">"{answers.learnings}"</p>
                          )}
                          {key === "struggles" && answers.current_state && (
                            <p className="text-sm text-[#111111]/70 leading-relaxed">"{answers.current_state}"</p>
                          )}
                          {key === "goals" && answers.forward && (
                            <p className="text-sm text-[#111111]/70 leading-relaxed">"{answers.forward}"</p>
                          )}
                        </div>
                      </div>
                    )
                  ))}

                  {!openingCheckin?.answers && (
                    <div className="bg-white border border-[#EBE3DF] rounded-2xl p-6 text-center">
                      <p className="text-[#111111]/50 text-sm">Du hoppade över incheckningen i början — men det du skrivit nu finns sparat i din resa.</p>
                    </div>
                  )}
                </div>

                <div className="text-center">
                  <Button
                    onClick={() => navigate("/avslutning")}
                    className="bg-[#A0655A] hover:bg-[#8B5549] text-white rounded-full px-8 py-6 text-base"
                    data-testid="go-to-completion-btn"
                  >
                    Se min avslutning
                    <ChevronRight size={18} className="ml-2" />
                  </Button>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
      <Footer />
    </div>
  );
}
